// Maps the backend's `structured_data` payload (shipped on every tool_result
// WS frame) into one of our typed BlockOutput variants. Pure — no React, no
// zustand.
//
// Entity drives the variant we emit. Unknown entities fall through to a
// generic actionResult block so the user at least sees something.

import type { BlockOutput } from '../../types';
import {
  buildCandidateBlock,
  buildCandidateTableBlock,
} from './candidateBlocks';
import { buildClarifyBlock, buildClarifyFormBlock } from './clarifyBlocks';
import { buildBulkOperationBlock } from './bulkOperationBlocks';
import { buildDashboardBlock } from './dashboardBlocks';
import { buildEmailComposeBlock } from './emailBlocks';
import {
  isContactsCreated,
  isStructuredBulkOperation,
  isStructuredClarify,
  isStructuredClarifyForm,
  isStructuredDashboard,
  isStructuredDataTable,
  isStructuredDownload,
  isStructuredEmailCompose,
  isStructuredIcp,
  isStructuredResume,
  isStructuredTable,
} from './guards';
import { buildInterviewListBlock } from './interviewBlocks';
import { buildIcpBlock, buildJobListBlock } from './jobBlocks';
import { buildContactsCreatedBlock, buildResumeBlock } from './resumeBlocks';
import {
  buildActionResultBlock,
  buildDataTableBlock,
  buildGenericTableBlock,
} from './tableBlocks';

/**
 * Convert backend `tool_result.structured_data` into a `BlockOutput`.
 * Returns `null` when the payload is missing, malformed, or empty so the
 * caller can skip pushing a useless block.
 */
export const structuredDataToBlock = (
  data: unknown,
  toolCallId?: string
): BlockOutput | null => {
  if (isStructuredBulkOperation(data)) {
    return buildBulkOperationBlock(data, toolCallId);
  }
  if (isStructuredDashboard(data)) {
    return buildDashboardBlock(data, toolCallId);
  }
  // Report artifact download card (export_report). The signed URL travels
  // only here — never through assistant prose.
  if (isStructuredDownload(data)) {
    return {
      type: 'download',
      data: { name: data.name, url: data.url, format: data.format },
    };
  }
  if (isStructuredClarify(data)) {
    return buildClarifyBlock(data, toolCallId);
  }
  if (isStructuredClarifyForm(data)) {
    return buildClarifyFormBlock(data, toolCallId);
  }
  if (isStructuredEmailCompose(data)) {
    return buildEmailComposeBlock(data, toolCallId);
  }
  if (isStructuredIcp(data)) {
    return buildIcpBlock(data, toolCallId);
  }
  if (isContactsCreated(data)) {
    return buildContactsCreatedBlock(data, toolCallId);
  }
  if (isStructuredResume(data)) {
    return buildResumeBlock(data, toolCallId);
  }
  // present_table must come before the kula_search table branch: its
  // discriminator is `type:"data_table"`, the search path keys off `type:"table"`.
  if (isStructuredDataTable(data)) {
    return buildDataTableBlock(data, toolCallId);
  }
  if (!isStructuredTable(data)) return null;
  const rows = data.rows ?? [];
  // Applications are candidates in a job context — same fields, same
  // click-to-expand UX — so they reuse the candidate card (hrefs already
  // include appId, giving a link straight to the pipeline-stage view).
  const isCandidateEntity =
    data.entity === 'contact' ||
    data.entity === 'candidate' ||
    data.entity === 'application';
  // The explicit filtered-search envelope can carry a meaningful zero count
  // without rows. Render that scoped empty state instead of dropping the
  // structured result and leaving only potentially ambiguous assistant prose.
  if (rows.length === 0) {
    return isCandidateEntity && data.result_scope === 'filtered_search'
      ? buildCandidateBlock(data, toolCallId)
      : null;
  }
  if (isCandidateEntity) {
    if (data.format === 'table') {
      return buildCandidateTableBlock(data, toolCallId);
    }
    return buildCandidateBlock(data, toolCallId);
  }
  if (data.entity === 'job' && data.format !== 'table') {
    return buildJobListBlock(data, toolCallId);
  }
  if (data.entity === 'interview' && data.format !== 'table') {
    return buildInterviewListBlock(data, toolCallId);
  }
  // Real tabular data (3+ columns) renders as a table. The chip fallback is
  // reserved for name-list and small-card shapes (created-project's
  // ["ID","Project"] block stays a chip — a table would surface the raw ID).
  if ((data.columns?.length ?? 0) >= 3 && (data.rows?.[0]?.length ?? 0) >= 3) {
    return buildGenericTableBlock(data, toolCallId);
  }
  return buildActionResultBlock(data, toolCallId);
};
